import { AttributeOffsets, attributesOf } from "./attributeOffsets";
import type { PredictorConfig } from "./config";
import type { Bean } from "./model";

/**
 * The learning rule, applied once per (bean, grinder) when the bean has dialed in. Mirrors OffsetLearning.java.
 *
 *   δ_realised = −b_final · (x_dialed − x_ref)
 *   residual   = δ_realised − δ_bean
 *   m          = number of the bean's non-unknown attributes
 *   for each:  observed = delta_ln + residual / m;  delta_ln ← (delta_ln·(n+k) + observed)/(n+k+1);  n += 1
 *
 * Pure; idempotence across launches is the caller's job (bean_learning rows).
 */
export interface LearningUpdate {
  readonly offsets: AttributeOffsets;
  readonly deltaRealised: number;
  readonly residual: number;
  readonly attributesUpdated: number;
}

export function learn(bean: Bean, bFinal: number, xDialed: number, xRef: number,
                      offsets: AttributeOffsets, cfg: PredictorConfig): LearningUpdate {
  const attrs = attributesOf(bean);
  const deltaBean = offsets.deltaBean(bean, cfg);
  const deltaRealised = -bFinal * (xDialed - xRef);
  const residual = deltaRealised - deltaBean;
  if (attrs.length === 0) return { offsets, deltaRealised, residual, attributesUpdated: 0 };

  const k = cfg.learningPseudoCount;
  let out = offsets;
  for (const av of attrs) {
    const o = out.get(av.attribute, av.value);
    const observed = o.deltaLn + residual / attrs.length;
    const updated = (o.deltaLn * (o.nBeans + k) + observed) / (o.nBeans + k + 1);
    out = out.withOffset({ attribute: av.attribute, value: av.value, deltaLn: updated, nBeans: o.nBeans + 1 });
  }
  return { offsets: out, deltaRealised, residual, attributesUpdated: attrs.length };
}
